/**
 * ColunasKanbanPanel — configura as colunas do kanban da Gestão (add/editar/
 * remover/reordenar), no estilo do "Configurar pipeline" do CRM. Cada coluna
 * tem título, cor e AGRUPA uma ou mais situações canônicas. As situações e suas
 * transições auditadas NÃO mudam — só o agrupamento visual em colunas.
 * Reordenação via @dnd-kit/sortable. Salva a config no backend (por unidade).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiAlertTriangle, FiInfo, FiMove, FiPlus, FiRotateCcw, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  obterConfigColunas,
  salvarConfigColunas,
} from "../../../services/solicitacoes";
import AjudaPharuCut from "./AjudaPharuCut";
import { analisarFluxo, contarAvisos } from "./fluxoPharuCut";

const COR_PADRAO = "#64748B";

let _seq = 0;
function novoId() {
  _seq += 1;
  return `col-${Date.now().toString(36)}-${_seq}`;
}

const Banner = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--info-bg);
  color: var(--info-text);
  border: 1px solid var(--info-border);
  font-size: 13px;
  line-height: 1.45;
  margin-bottom: 14px;
  svg { flex: none; margin-top: 2px; }
`;

const ErroBanner = styled(Banner)`
  background: var(--danger-bg);
  color: var(--danger-text);
  border-color: var(--danger-border);
`;

const Rotulo = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin: 6px 0 10px;
`;

const ColunaCard = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 8px;
`;

const LinhaTopo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Handle = styled.button`
  flex: none;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: var(--ink-3);
  cursor: grab;
  &:active { cursor: grabbing; }
`;

const CorInput = styled.input`
  flex: none;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: none;
  cursor: pointer;
`;

const TituloInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-size: 14px;
  &:focus { outline: none; border-color: var(--accent); }
`;

const IconBtn = styled.button`
  flex: none;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: ${({ $danger }) => ($danger ? "var(--danger-text)" : "var(--ink-2)")};
  cursor: pointer;
  &:hover { border-color: var(--accent); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ChipsSituacao = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 36px;
`;

const SitChip = styled.button`
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--ink-3)")};
`;

const BlocoCut = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 36px;
`;

const RotuloCut = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--ink-2);
`;

const LinhaCut = styled.div`
  display: flex;
  select {
    flex: 1;
    min-width: 0;
    height: 32px;
    padding: 0 8px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--ink);
    font-size: 13px;
  }
`;

const FraseCut = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--ink-3);
`;

const AvisoCut = styled.p`
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--warning-text);
  svg { flex: none; margin-top: 2px; }
`;

const ResumoAvisos = styled(Banner)`
  background: var(--warning-bg);
  color: var(--warning-text);
  border-color: var(--warning-border);
`;

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  border-radius: 10px;
  border: 1px dashed var(--accent);
  background: transparent;
  color: var(--accent);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
`;

const Aviso = styled.div`
  font-size: 12.5px;
  color: var(--warning-text, var(--ink-2));
  margin-top: 10px;
`;

const Rodape = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const RestaurarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: var(--accent); color: var(--accent); }
`;

function ColunaRow({ coluna, situacoes, eventosCut, leituraCut, podeExcluir, onMudar, onToggleSit, onExcluir }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: coluna.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.6, zIndex: 2 } : null),
  };
  return (
    <ColunaCard ref={setNodeRef} style={style}>
      <LinhaTopo>
        <Handle type="button" aria-label="Reordenar coluna" {...attributes} {...listeners}>
          <FiMove size={14} aria-hidden="true" />
        </Handle>
        <CorInput
          type="color"
          value={coluna.cor || COR_PADRAO}
          onChange={(e) => onMudar({ cor: e.target.value })}
          aria-label="Cor da coluna"
          title="Cor da coluna"
        />
        <TituloInput
          value={coluna.titulo}
          onChange={(e) => onMudar({ titulo: e.target.value })}
          placeholder="Título da coluna"
          maxLength={60}
          aria-label="Título da coluna"
        />
        <IconBtn
          type="button"
          $danger
          onClick={onExcluir}
          disabled={!podeExcluir}
          title={podeExcluir ? "Remover coluna" : "Não é possível remover a única coluna"}
          aria-label="Remover coluna"
        >
          <FiTrash2 size={13} aria-hidden="true" />
        </IconBtn>
      </LinhaTopo>
      <ChipsSituacao>
        {situacoes.map((s) => (
          <SitChip
            key={s.value}
            type="button"
            $ativo={(coluna.situacoes || []).includes(s.value)}
            onClick={() => onToggleSit(s.value)}
          >
            {s.label}
          </SitChip>
        ))}
      </ChipsSituacao>
      {eventosCut.length > 0 && (
        <BlocoCut>
          <RotuloCut>
            <label htmlFor={`cut-${coluna.id}`}>PharuCut traz o card para cá quando o lote:</label>
            <AjudaPharuCut eventos={eventosCut} />
          </RotuloCut>
          <LinhaCut>
            <select
              id={`cut-${coluna.id}`}
              value={coluna.evento_cut || ""}
              onChange={(e) => onMudar({ evento_cut: e.target.value })}
            >
              <option value="">— não muda sozinha (só arrastando) —</option>
              {eventosCut.map((ev) => (
                <option key={ev.value} value={ev.value}>
                  {ev.label}
                </option>
              ))}
            </select>
          </LinhaCut>
          {leituraCut?.frase ? <FraseCut>{leituraCut.frase}</FraseCut> : null}
          {(leituraCut?.avisos || []).map((aviso) => (
            <AvisoCut key={aviso}>
              <FiAlertTriangle size={13} aria-hidden="true" />
              <span>{aviso}</span>
            </AvisoCut>
          ))}
        </BlocoCut>
      )}
    </ColunaCard>
  );
}

const comId = (arr) =>
  (arr || []).map((c) => ({
    id: c.id || novoId(),
    titulo: c.titulo || "",
    cor: c.cor || COR_PADRAO,
    situacoes: Array.isArray(c.situacoes) ? [...c.situacoes] : [],
    evento_cut: c.evento_cut || "",
  }));

export default function ColunasKanbanPanel({ open, tipo = "", onClose, onSaved }) {
  const { request } = useAuth();
  const [colunas, setColunas] = useState([]);
  const [situacoes, setSituacoes] = useState([]);
  const [eventosCut, setEventosCut] = useState([]);
  const [padrao, setPadrao] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setLoading(true);
    setErro("");
    obterConfigColunas(request, tipo)
      .then((d) => {
        if (!vivo) return;
        setColunas(comId(d.colunas));
        setSituacoes(d.situacoes || []);
        setEventosCut(d.eventos_cut || []);
        setPadrao(d.padrao || []);
      })
      .catch((e) => vivo && setErro(e?.message || "Erro ao carregar as colunas."))
      .finally(() => {
        if (vivo) setLoading(false);
      });
    return () => {
      vivo = false;
    };
  }, [open, request, tipo]);

  const mudarColuna = useCallback((id, patch) => {
    setColunas((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }, []);

  // Alterna uma situação numa coluna (NÃO exclusiva — a mesma situação pode
  // ancorar várias colunas; o card cai por etapa_quadro).
  const toggleSituacao = useCallback((id, situacao) => {
    setColunas((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              situacoes: c.situacoes.includes(situacao)
                ? c.situacoes.filter((s) => s !== situacao)
                : [...c.situacoes, situacao],
            }
          : c,
      ),
    );
  }, []);

  const removerColuna = useCallback((id) => {
    setColunas((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const adicionarColuna = useCallback(() => {
    setColunas((prev) => [
      ...prev,
      { id: novoId(), titulo: "", cor: COR_PADRAO, situacoes: [], evento_cut: "" },
    ]);
  }, []);

  const restaurarPadrao = useCallback(() => setColunas(comId(padrao)), [padrao]);

  const aoReordenar = useCallback((ev) => {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;
    setColunas((prev) => {
      const a = prev.findIndex((c) => c.id === active.id);
      const b = prev.findIndex((c) => c.id === over.id);
      if (a === -1 || b === -1) return prev;
      return arrayMove(prev, a, b);
    });
  }, []);

  const leituraCut = useMemo(() => analisarFluxo(colunas, eventosCut), [colunas, eventosCut]);
  const avisosCut = contarAvisos(leituraCut);

  const naoAtribuidas = useMemo(() => {
    const usadas = new Set(colunas.flatMap((c) => c.situacoes));
    return situacoes.filter((s) => !usadas.has(s.value));
  }, [colunas, situacoes]);

  const salvar = useCallback(async () => {
    if (colunas.length === 0) {
      setErro("Defina ao menos uma coluna.");
      throw new Error("Defina ao menos uma coluna.");
    }
    if (colunas.some((c) => !c.titulo.trim())) {
      setErro("Todas as colunas precisam de um título.");
      throw new Error("Todas as colunas precisam de um título.");
    }
    setErro("");
    const payload = colunas.map((c) => ({
      id: c.id,
      titulo: c.titulo.trim(),
      cor: c.cor || COR_PADRAO,
      situacoes: c.situacoes,
      evento_cut: c.evento_cut || "",
    }));
    await salvarConfigColunas(request, tipo, payload);
    onSaved?.();
    onClose?.();
  }, [colunas, request, tipo, onSaved, onClose]);

  const footer = (
    <Rodape>
      <RestaurarBtn type="button" onClick={restaurarPadrao} disabled={!padrao.length}>
        <FiRotateCcw size={14} /> Restaurar padrão
      </RestaurarBtn>
      <AsyncButton
        variant="primary"
        silentError
        successMessage="Colunas do kanban salvas."
        onClick={salvar}
        disabled={loading || !colunas.length}
      >
        Salvar colunas
      </AsyncButton>
    </Rodape>
  );

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title="Configurar colunas do kanban"
      subtitle={
        tipo && tipo !== "TODOS"
          ? `Colunas do tipo ${tipo} — adicione, edite, reordene e remova.`
          : "Colunas de todos os tipos (config global) — edite à vontade."
      }
      width="clamp(560px, 46vw, 96vw)"
      footer={footer}
    >
      <Banner>
        <FiInfo size={16} aria-hidden="true" />
        <span>
          Monte as colunas livremente. Marque em cada uma os{" "}
          <strong>status</strong> que ela representa: arrastar um card para ela
          dispara a transição (autorizar, atender, recusar, cancelar). Colunas{" "}
          <strong>sem status</strong> são etapas operacionais: o card fica lá sem
          mudar o status. Para o card andar sozinho com o lote, use o campo{" "}
          <strong>PharuCut</strong> de cada coluna; o <strong>?</strong> ao lado dele
          explica com um exemplo.
        </span>
      </Banner>

      {erro && (
        <ErroBanner>
          <FiInfo size={16} aria-hidden="true" />
          <span>{erro}</span>
        </ErroBanner>
      )}

      {loading ? (
        <Rotulo>Carregando colunas…</Rotulo>
      ) : (
        <>
          {avisosCut > 0 && (
            <ResumoAvisos role="status">
              <FiAlertTriangle size={16} aria-hidden="true" />
              <span>
                {avisosCut === 1 ? "1 coluna ligada ao PharuCut não vai funcionar" : `${avisosCut} avisos na ligação com o PharuCut`}{" "}
                como parece. Veja o aviso em amarelo na coluna antes de salvar.
              </span>
            </ResumoAvisos>
          )}
          <Rotulo>Colunas · {colunas.length}</Rotulo>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={aoReordenar}>
            <SortableContext
              items={colunas.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {colunas.map((coluna) => (
                <ColunaRow
                  key={coluna.id}
                  coluna={coluna}
                  situacoes={situacoes}
                  eventosCut={eventosCut}
                  leituraCut={leituraCut[coluna.id]}
                  podeExcluir={colunas.length > 1}
                  onMudar={(patch) => mudarColuna(coluna.id, patch)}
                  onToggleSit={(s) => toggleSituacao(coluna.id, s)}
                  onExcluir={() => removerColuna(coluna.id)}
                />
              ))}
            </SortableContext>
          </DndContext>

          <AddBtn type="button" onClick={adicionarColuna}>
            <FiPlus size={15} /> Adicionar coluna
          </AddBtn>

          {naoAtribuidas.length > 0 && (
            <Aviso>
              Sem coluna (ficam escondidas no quadro):{" "}
              {naoAtribuidas.map((s) => s.label).join(", ")}.
            </Aviso>
          )}
        </>
      )}
    </SlideOverPanel>
  );
}
